import {
  Page,
  LayoutSingleColumn,
  Heading,
  IconShield,
  IconLocation,
  IconLightning,
  IconSearch,
  IconMotorcycle,
} from '../../components';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';

import css from './SafetyPage.module.css';

const SafetyPage = () => {
  return (
    <Page
      title="Trust & Safety Guide"
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: 'UsedEmoto Trust & Safety Guide for buying and selling electric motorcycles',
        name: 'Trust & Safety Guide',
      }}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.mainWrapper}>
          <Heading as="h1" className={css.heading}>
            Trust & Safety Guide
          </Heading>

          <p className={css.leadText}>
            UsedEmoto was built by riders to provide a safe, specialized alternative to anonymous social
            media marketplaces. Follow these proven safety guidelines when buying, selling, or test riding
            used e-motos.
          </p>

          <ul className={css.checklist}>
            <li className={css.checklistItem}>
              <IconShield className={css.iconSvg} />
              <div>
                <strong>Never send a deposit before inspecting the bike in person.</strong> Avoid wire
                transfers, gift cards, or unverified remote payment requests from sellers you haven't met.
              </div>
            </li>
            <li className={css.checklistItem}>
              <IconLocation className={css.iconSvg} />
              <div>
                <strong>Meet in a public, well-lit location during daylight.</strong> Police station
                safe-exchange zones, busy bank parking lots, or local motorcycle shop parking areas are ideal.
              </div>
            </li>
            <li className={css.checklistItem}>
              <IconLightning className={css.iconSvg} />
              <div>
                <strong>Inspect the battery pack, charger, and BMS thoroughly.</strong> Check for pack swelling,
                aftermarket wiring splices, mismatched voltages, or error codes on display controllers.
              </div>
            </li>
            <li className={css.checklistItem}>
              <IconSearch className={css.iconSvg} />
              <div>
                <strong>Verify the VIN / serial number and title paperwork.</strong> Check frame headtube stamps
                against the seller's bill of sale or title to ensure the bike is not stolen or salvaged.
              </div>
            </li>
            <li className={css.checklistItem}>
              <IconMotorcycle className={css.iconSvg} />
              <div>
                <strong>Follow safe test-ride protocols.</strong> Sellers should hold collateral (cash in hand
                or driver's license + car keys) and agree on a closed street or parking lot perimeter before handing
                over the throttle.
              </div>
            </li>
          </ul>

          <section>
            <Heading as="h2" className={css.subHeading}>
              1. Meeting & Location Guidelines
            </Heading>
            <p className={css.paragraph}>
              Nearly 95% of e-moto transactions happen in person via local pickup. Always choose a neutral public
              location with security cameras. If you must meet at a residential address, bring a friend or family
              member along.
            </p>
          </section>

          <section>
            <Heading as="h2" className={css.subHeading}>
              2. Technical Inspection Checklist
            </Heading>
            <p className={css.paragraph}>
              Light electric motorcycles have unique components that dictate their value. Before completing a
              purchase:
            </p>
            <p className={css.paragraph}>
              • <strong>Battery Health:</strong> Ask the seller to charge the bike to 100% prior to meeting. Verify
              the full resting voltage (e.g. ~67.2V for standard 60V packs, ~84.0V for 72V packs).
            </p>
            <p className={css.paragraph}>
              • <strong>Charger:</strong> Confirm the included charger matches the pack chemistry, connector, and
              voltage.
            </p>
            <p className={css.paragraph}>
              • <strong>Controller & Error Codes:</strong> Turn the bike on and check the display screen for error
              codes (hall sensor, throttle voltage, brake cutoffs).
            </p>
            <p className={css.paragraph}>
              • <strong>Frame & Suspension:</strong> Check headtube weld seams, swingarm pivot play, and fork seals
              for leaks or hard bottom-out damage.
            </p>
          </section>

          <section>
            <Heading as="h2" className={css.subHeading}>
              3. Spotting & Reporting Scams
            </Heading>
            <p className={css.paragraph}>
              Common warning signs include: prices drastically below fair market value (e.g., a $2,000 Stark Varg
              or $1,500 Ultra Bee), requests for courier delivery with prepaid gift cards, or refusal to do a live
              video call or in-person meetup.
            </p>
            <p className={css.paragraph}>
              If you suspect a listing is fraudulent, stolen, or violates community standards, report it immediately
              using the "Report this listing" button on the listing page or contact us directly at
              tech@usedemoto.com.
            </p>
          </section>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default SafetyPage;
